/**
 * Meine Aufgaben – Einstiegsseite.
 *
 * Hieß bis 31.08.2026 „Mein Tag". Ein Zeitraum benennt keine Handlung: Die
 * Seite sagt jetzt, was zu tun ist, und trägt auch, wenn später der Kalkulant
 * hier „Angebot überarbeiten" bekommt und das Kaufmännische die Belegzuordnung.
 *
 * Die Grundregel, die das ganze Verhalten trägt: Ein Angebot verlässt diese
 * Seite ausschließlich durch Auftrag, Absage, Wiedervorlage mit festem Datum
 * oder Rückgabe in die Kalkulation. Nachfassen allein entfernt nichts – der
 * Kunde hat ja noch nicht geantwortet, und eine Rückmeldung lässt sich nicht
 * erzwingen. Ein früherer Entwurf hatte einen „Erledigt"-Haken; der war
 * fachlich falsch und ist ersatzlos gestrichen.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconInfo, IconTelefon, IconMail } from '../komponenten/Icons';
import { eur, whatsappNummer } from '../hilfen/format';
import { liegestufe } from '../hilfen/liegezeit';
import { AnrufModal } from '../modals/AnrufModal';
import { ErinnerungModal } from '../modals/ErinnerungModal';
import { SummeModal } from '../modals/SummeModal';
import { VerlaufModal } from '../modals/VerlaufModal';
import type { Angebot } from '../typen';
import './meineaufgaben.css';

export function MeineAufgaben() {
  const { nutzer, modalOeffnen, darf } = useKora();
  const meta = useMeta();
  const navigate = useNavigate();

  const [rueckmeldung, setRueckmeldung] = useState<Angebot | null>(null);
  const [mailFuer, setMailFuer] = useState<Angebot | null>(null);
  const [summeFuer, setSummeFuer] = useState<Angebot | null>(null);
  const [verlaufFuer, setVerlaufFuer] = useState<Angebot | null>(null);
  const [laeuftOffen, setLaeuftOffen] = useState(true);

  const abfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
    enabled: darf('monitor'),
  });

  const angebote = useMemo(() => abfrage.data ?? [], [abfrage.data]);
  const meinName = nutzer?.name ?? '';

  /** „Meine" Vorgänge: die, die ich führe – Versender, ersatzweise Kalkulant. */
  const meine = useMemo(
    () => angebote.filter((a) => a.beimKunden && (a.versender || a.kalkulant) === meinName),
    [angebote, meinName],
  );

  /*
   * Zwei Abschnitte, ein einziges Kriterium: Steht ein Termin in der Zukunft?
   *
   * Abgeleitet, nirgends gespeichert – wie Liegezeit, Eskalationsstufe und
   * Nachfass-Runden auch. Genau dort entstanden im Prototyp die
   * Widersprüche: je eine Funktion pro abgeleitetem Wert, sonst gar keine.
   */
  const jetzt = useMemo(
    () => [...meine].filter((a) => !a.laeuftNoch).sort((x, y) => y.tageVersand - x.tageVersand),
    [meine],
  );
  const laeuft = useMemo(
    () =>
      [...meine]
        .filter((a) => a.laeuftNoch)
        .sort((x, y) => (x.faellig ?? '').localeCompare(y.faellig ?? '')),
    [meine],
  );

  /*
   * Die Kopfsumme sinkt beim Nachfassen nicht – das Geld liegt weiter beim
   * Kunden. Sie ändert sich nur bei Auftrag oder Absage.
   */
  const mitSumme = meine.filter((a) => a.summe !== null);
  const gesamt = mitSumme.reduce((s, a) => s + (a.summe ?? 0), 0);
  const ohneSumme = meine.length - mitSumme.length;

  const { orangeBis } = meta.liegezeit;
  const lange = jetzt.filter((a) => a.tageVersand > orangeBis).length;

  /**
   * Team-Überblick – für alle sichtbar, ohne Rechteprüfung.
   *
   * Jeder soll sehen, wie die Angebote der anderen stehen; das verhindert
   * Doppelarbeit. Beschränkt bleiben nur Auftragsübersicht und Finanzen, also
   * die Bereiche mit Beträgen. Der eigene Eintrag entfällt – er wäre dieselbe
   * Aussage wie die Karte links.
   */
  const team = useMemo(() => {
    const beimKunden = angebote.filter((a) => a.beimKunden);
    return meta.team
      .filter((p) => p.imVertrieb && p.name !== meinName)
      .map((p) => {
        const seine = beimKunden.filter((a) => (a.versender || a.kalkulant) === p.name);
        return {
          ...p,
          anzahl: seine.length,
          summe: seine.reduce((s, a) => s + (a.summe ?? 0), 0),
          faellig: seine.filter((a) => !a.laeuftNoch).length,
        };
      })
      .filter((t) => t.anzahl > 0)
      .sort((x, y) => y.faellig - x.faellig || y.anzahl - x.anzahl);
  }, [angebote, meta.team, meinName]);

  function nummerVon(a: Angebot): string {
    return a.kunde.telefon || a.kunde.mobil || a.kunde.mobilGeschaeftlich || '';
  }

  const zeilenEigenschaften = {
    onSumme: setSummeFuer,
    onMail: setMailFuer,
    onRueckmeldung: setRueckmeldung,
    onVerlauf: setVerlaufFuer,
    nummerVon,
    orangeBis,
    gruenBis: meta.liegezeit.gruenBis,
  };

  return (
    <div className="mt">
      <div className="mt-kopf">
        <div>
          <div className="mt-kicker">{langesDatum(meta.heute)}</div>
          <h1 className="mt-gruss">Willkommen, {nutzer?.name}</h1>
        </div>
        {darf('monitor') && (
          <button type="button" className="mt-ausgang" onClick={() => navigate('/cockpit')}>
            Alle Vorgänge im Vertriebscockpit →
          </button>
        )}
      </div>

      {!darf('monitor') ? (
        <div className="eh-karte">
          Für dich ist der Vertriebsbereich nicht freigeschaltet. Die Freigabe erteilt die
          Administration im Admin Center.
        </div>
      ) : (
        <div className="mt-grid">
          <div className="mt-tisch">
            <div className="mt-tisch-kopf">
              <div>
                <div className="mt-label">Auf deinem Tisch</div>
                <div className="mt-zahl-zeile">
                  <span className={`mt-zahl ${meine.length ? '' : 'ruhig'}`}>{meine.length}</span>
                  <span className="mt-zahl-text">
                    {meine.length === 1
                      ? 'Angebot liegt beim Kunden'
                      : 'Angebote liegen beim Kunden'}
                  </span>
                </div>
                <div className="mt-basis">
                  {mitSumme.length === 0
                    ? 'Noch keine Angebotssumme erfasst'
                    : `Gesamtwert ${eur(gesamt)} netto` +
                      (ohneSumme > 0
                        ? ` aus ${mitSumme.length} von ${meine.length} Angeboten · ` +
                          `${ohneSumme} aus Monday übernommen, Summe fehlt`
                        : ' · unverändert, bis Auftrag oder Absage feststeht')}
                </div>
              </div>

              {lange > 0 && (
                <span className="mt-eskalation">
                  <span className="mt-eskalation-punkt" />
                  {lange} länger als {orangeBis} Tage
                </span>
              )}
            </div>

            <div className="mt-abschnitt-kopf">
              <span className="mt-abschnitt-titel">
                Jetzt dran · {jetzt.length} {jetzt.length === 1 ? 'Vorgang' : 'Vorgänge'}
              </span>
            </div>

            <div className="mt-liste">
              {jetzt.map((a) => (
                <Zeile key={a.id} a={a} {...zeilenEigenschaften} />
              ))}
              {jetzt.length === 0 && (
                <div className="mt-leer">
                  Gerade ist nichts dran. Was einen Termin hat, steht unten und kommt von selbst
                  zurück.
                </div>
              )}
            </div>

            {laeuft.length > 0 && (
              <>
                <button
                  type="button"
                  className="mt-abschnitt-kopf mt-abschnitt-knopf"
                  onClick={() => setLaeuftOffen((o) => !o)}
                >
                  <span className="mt-pfeil">{laeuftOffen ? '▾' : '▸'}</span>
                  <span className="mt-abschnitt-titel gedaempft">
                    Läuft noch · {laeuft.length} {laeuft.length === 1 ? 'Angebot' : 'Angebote'}
                  </span>
                  <span className="mt-abschnitt-zusatz">kommen automatisch zurück</span>
                </button>

                {laeuftOffen && (
                  <div className="mt-liste mt-liste--ruhig">
                    {laeuft.map((a) => (
                      <Zeile key={a.id} a={a} {...zeilenEigenschaften} ruhig />
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="mt-fussnote">
              Kein Angebot verschwindet von selbst – es bleibt hier, bis ein Auftrag, eine Absage
              oder eine Wiedervorlage erfasst ist.
            </div>
          </div>

          <div className="mt-seitenspalte">
            <div className="mt-karte">
              <div className="mt-label">Team-Überblick</div>
              <div className="mt-team">
                {team.map((p) => (
                  <div className="mt-person" key={p.name}>
                    <span className="mt-avatar">{p.initialen}</span>
                    <span className="mt-person-text">
                      <span className="mt-person-name">{p.name}</span>
                      <span className="mt-person-meta">
                        {p.anzahl} {p.anzahl === 1 ? 'Angebot' : 'Angebote'} beim Kunden
                      </span>
                    </span>
                    <span className="mt-person-zahlen">
                      <span className="mt-person-summe">{p.summe > 0 ? eur(p.summe) : '—'}</span>
                      <span className={`mt-person-faellig ${p.faellig ? 'offen' : ''}`}>
                        {p.faellig === 0
                          ? 'nichts fällig'
                          : p.faellig === 1
                            ? '1 Vorgang fällig'
                            : `${p.faellig} Vorgänge fällig`}
                      </span>
                    </span>
                  </div>
                ))}

                {team.length === 0 && (
                  <div className="mt-leer">Bei den anderen liegt gerade kein Angebot.</div>
                )}
              </div>

              <div className="mt-team-hinweis">
                Deine eigenen Vorgänge stehen links unter „Auf deinem Tisch" – hier stehen nur die
                anderen.
              </div>
            </div>

            <div className="mt-fuss">
              <span>
                Kora {meta.version}
                {meta.trockenlauf ? ' · Testbetrieb, Kundenmails werden nicht versendet' : ''}
              </span>
              <button
                type="button"
                className="mt-info"
                title="Was ist neu?"
                onClick={() => modalOeffnen({ art: 'changelog', alle: true })}
              >
                <IconInfo />
              </button>
            </div>
          </div>
        </div>
      )}

      {rueckmeldung && (
        <AnrufModal
          angebot={rueckmeldung}
          onSchliessen={() => setRueckmeldung(null)}
          onFertig={() => abfrage.refetch()}
        />
      )}

      {mailFuer && (
        <ErinnerungModal
          angebot={mailFuer}
          onSchliessen={() => setMailFuer(null)}
          onFertig={() => abfrage.refetch()}
        />
      )}

      {summeFuer && (
        <SummeModal
          angebot={summeFuer}
          onSchliessen={() => setSummeFuer(null)}
          onFertig={() => abfrage.refetch()}
        />
      )}

      {verlaufFuer && (
        <VerlaufModal angebot={verlaufFuer} onSchliessen={() => setVerlaufFuer(null)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

interface ZeilenProps {
  a: Angebot;
  ruhig?: boolean;
  gruenBis: number;
  orangeBis: number;
  nummerVon: (a: Angebot) => string;
  onSumme: (a: Angebot) => void;
  onMail: (a: Angebot) => void;
  onRueckmeldung: (a: Angebot) => void;
  onVerlauf: (a: Angebot) => void;
}

/**
 * Eine Zeile der Liste.
 *
 * Einzeilig: Kunde und Ort stecken bereits in der Angebotsnummer
 * („26073_LD_Wratschko (Neusiedl am See)"). Eine Unterzeile mit denselben
 * Angaben wäre Wiederholung und kostete 18 Pixel je Zeile.
 *
 * Farbe trägt genau ein Element: die Liegezeit. Die Nummer bleibt schwarz, weil
 * sie die Identität ist und immer gleich wichtig – eine rote Nummer neben einer
 * grünen Zeitangabe wären zwei Signale mit widersprüchlicher Aussage in
 * derselben Zeile.
 */
function Zeile({
  a,
  ruhig,
  gruenBis,
  orangeBis,
  nummerVon,
  onSumme,
  onMail,
  onRueckmeldung,
  onVerlauf,
}: ZeilenProps) {
  const tel = nummerVon(a);

  return (
    <div className={`mt-zeile ${ruhig ? 'mt-zeile--ruhig' : ''}`}>
      <button type="button" className="mt-nummer" title={a.name} onClick={() => onVerlauf(a)}>
        {a.name}
      </button>

      <div className="mt-betrag">
        {a.summe === null ? (
          <button
            type="button"
            className="mt-summe-fehlt"
            title="Angebotssumme nachtragen"
            onClick={() => onSumme(a)}
          >
            + Summe
          </button>
        ) : (
          <span title={`${a.summeText} netto`}>{a.summeText}</span>
        )}
      </div>

      <div className="mt-stand mt-weg">
        {ruhig ? (
          <>
            <span className="mt-wieder" title={`Wieder auf deinem Tisch am ${a.faelligText}`}>
              wieder am {kurzDatum(a.faelligText)}
            </span>
            <span className="mt-nachfass" title={nachfassText(a)}>
              {nachfassText(a)}
            </span>
          </>
        ) : (
          <>
            <span
              className={`mt-liegezeit ${liegestufe(a.tageVersand, gruenBis, orangeBis)}`}
              title={`Seit ${a.tageVersand} Tagen beim Kunden, versendet am ${a.versandDatum}`}
            >
              seit {a.tageVersand} {a.tageVersand === 1 ? 'Tag' : 'Tagen'} beim Kunden
            </span>
            <span className="mt-nachfass" title={nachfassText(a)}>
              {nachfassText(a)}
            </span>
          </>
        )}
      </div>

      <div className="mt-aktionen">
        <a
          className={`mt-aktion ${tel ? '' : 'aus'}`}
          href={tel ? `tel:${whatsappNummer(tel)}` : undefined}
          title={tel ? `${tel} anrufen` : 'Keine Telefonnummer hinterlegt'}
        >
          <IconTelefon groesse={15} />
        </a>
        <button
          type="button"
          className={`mt-aktion mt-weg ${a.kunde.mail ? '' : 'aus'}`}
          title={a.kunde.mail ? 'Erinnerung ansehen und versenden' : 'Keine E-Mail-Adresse hinterlegt'}
          onClick={() => a.kunde.mail && onMail(a)}
        >
          <IconMail groesse={15} />
        </button>
        <button
          type="button"
          className="mt-aktion mt-aktion--haupt"
          title="Rückmeldung erfassen"
          onClick={() => onRueckmeldung(a)}
        >
          <IconSprechblase groesse={15} />
        </button>
      </div>
    </div>
  );
}

/**
 * Nachfass-Stand als Zustand, nicht als Rundennummer.
 *
 * Frühere Fassungen schrieben „1. Nachfassen". Das ist mehrdeutig – gemeint war
 * die anstehende Runde, gelesen wurde die erfolgte.
 */
function nachfassText(a: Angebot): string {
  if (a.summe === null && a.nachfassRunden === 0 && a.versenderQuelle === 'monday') {
    return 'Monday-Import';
  }
  if (a.nachfassRunden === 0) return 'noch nicht nachgefasst';
  return `${a.nachfassRunden}× nachgefasst · ${kurzDatum(a.nachfassLetzteText)}`;
}

/**
 * Datum ausgeschrieben, mit Wochentag: „Montag, 31. August 2026".
 *
 * Gerechnet wird aus dem Serverdatum, nicht aus der Systemuhr des Arbeitsplatzes
 * – sonst stünde bei falsch gestellter Uhr ein anderer Tag im Kopf als in der
 * Liegezeit darunter.
 */
function langesDatum(iso: string): string {
  const tage = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const monate = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
  ];
  const d = new Date(iso + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return `${tage[d.getDay()]}, ${d.getDate()}. ${monate[d.getMonth()]} ${d.getFullYear()}`;
}

/** „31.08.26" wird zu „31. Aug." – in der Zeile zählt jedes Zeichen. */
function kurzDatum(text: string): string {
  const teile = text.split('.');
  if (teile.length < 2) return text;
  const monate = ['Jän.', 'Feb.', 'Mär.', 'Apr.', 'Mai', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];
  const monat = monate[Number(teile[1]) - 1];
  return monat ? `${Number(teile[0])}. ${monat}` : text;
}


/** Sprechblase – steht für „Rückmeldung erfassen". */
function IconSprechblase({ groesse = 15 }: { groesse?: number }) {
  return (
    <svg
      width={groesse}
      height={groesse}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path d="M20 12a7.5 7.5 0 0 1-7.5 7.5H8l-4 3v-4.4A7.5 7.5 0 0 1 12.5 4.5 7.5 7.5 0 0 1 20 12Z" />
    </svg>
  );
}